import { z } from "zod";

import type { AuthSession, LoginResult } from "../types/auth.types";

const membershipSchema = z.object({
  tenantId: z.uuid(),
  tenantName: z.string(),
  adminTitle: z.string().max(128).nullable().optional().default(null),
  // Unknown backend roles remain data; they do not create UI permissions.
  roles: z.array(z.string().min(1)),
});
const userSchema = z.object({
  personId: z.uuid(),
  displayName: z.string(),
  username: z.string().min(1),
  tenants: z
    .array(membershipSchema)
    .refine(
      (tenants) =>
        new Set(tenants.map((tenant) => tenant.tenantId)).size ===
        tenants.length,
      "Duplicate tenant memberships",
    ),
});

/** Validates the login boundary without changing the generated wire contract. */
export const loginResultSchema = userSchema.extend({
  accessToken: z.string().trim().min(1),
  expiresInSeconds: z
    .union([z.number(), z.string().regex(/^\d+$/)])
    .transform(Number)
    .pipe(
      z
        .number()
        .int()
        .positive()
        .max(Number.MAX_SAFE_INTEGER / 1000),
    ),
});

/** Persisted identity selects an actual membership, or null for an empty portfolio. */
export const sessionSchema = z
  .object({
    user: userSchema,
    accessToken: z.string().trim().min(1),
    expiresAt: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    activeTenantId: z.uuid().nullable(),
  })
  .refine(
    (session) =>
      session.user.tenants.length === 0
        ? session.activeTenantId === null
        : session.user.tenants.some(
            (tenant) => tenant.tenantId === session.activeTenantId,
          ),
    "The active tenant must belong to the session",
  );

/** Converts a validated response using the configurable server lifetime, with no invented fallback. */
export function createAuthSession(
  result: LoginResult,
  now = Date.now(),
): AuthSession {
  const parsed = loginResultSchema.parse(result);
  const { accessToken, expiresInSeconds, ...user } = parsed;
  return sessionSchema.parse({
    user,
    accessToken,
    expiresAt: now + expiresInSeconds * 1000,
    // Preserve the existing first-membership default; routing uses this same tenant.
    activeTenantId: user.tenants[0]?.tenantId ?? null,
  });
}
